import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SchoolRosterStudent, ModalType, ToastMessageType, TransferStatus } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';
import { ModalDialogComponent } from '@k12/app-components';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-cancel-transfer-actions',
  templateUrl: './cancel-transfer-actions.component.html',
  styleUrl: './cancel-transfer-actions.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule]
})
export class CancelTransferActionsComponent {
  @Input() student: SchoolRosterStudent | null = null;
  @Output() canceled = new EventEmitter<string>();

  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly matDialog = inject(MatDialog);

  isCanceling = false;
  private confirmDialogRef?: MatDialogRef<ModalDialogComponent>;

  get canCancel(): boolean {
    // Cannot cancel if student is null or status is Complete (ID = 1)
    return !!this.student && this.student.transferStatusId !== TransferStatus.Complete;
  }

  onCancel(): void {
    if (!this.canCancel || this.isCanceling || !this.student) return;

    this.showCancelConfirmationModal();
  }

  private showCancelConfirmationModal(): void {
    this.confirmDialogRef = this.matDialog.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        align: 'center',
        checkMark: false,
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        message: 'Are you sure you want to cancel this transfer/withdrawal? This action cannot be undone.',
        modalType: ModalType.ConfirmModal,
        title: 'Confirm Cancellation'
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    this.confirmDialogRef.componentInstance.onConfirmModal.subscribe(() => {
      this.confirmDialogRef?.close();
      this.processCancellation();
    });

    this.confirmDialogRef.componentInstance.onCancelModal.subscribe(() => {
      this.confirmDialogRef?.close();
    });
  }

  private processCancellation(): void {
    if (!this.student) return;

    this.isCanceling = true;

    this.schoolsService.cancelTransferWithdrawal(this.student.studentEnrollmentId).subscribe({
      next: () => {
        this.isCanceling = false;
        this.toastMessageService.showToast(ToastMessageType.Success, 'Transfer/withdrawal canceled successfully');
        if (this.student) {
          this.canceled.emit(this.student.studentEnrollmentId);
        }
      },
      error: () => {
        this.isCanceling = false;
        this.toastMessageService.showToast(
          ToastMessageType.Error,
          'An error occurred while canceling the transfer/withdrawal'
        );
      }
    });
  }
}
